import { Box, Button, Center, Heading, HStack, Icon, Input, Text, } from "@chakra-ui/react";
import { formatPrice, } from "../../../../utils/formatPrice.ts";
import { CheckCircleIcon, } from "@heroicons/react/24/solid";
import { ExclamationCircleIcon, } from "@heroicons/react/24/solid";
import { useQrPaymentMutation, } from "../../hooks";
import { PageLoader, } from "../../../shared/PageLoader";
import { useMutation, } from "@tanstack/react-query";
import { qrPaymentMutationFn, } from "../../../../api";
import { useEffect, useState, } from "react";

export interface IQrPaymentModalProps {
  onClose?: () => void;
  /** children can use this callback to prevent modal from being closed by the backdrop */
  block?: (state: boolean) => void;
}

const formatAmountWithoutCurrency = (amount: number,): string => {
  return formatPrice(amount, 0,).replace(/\s₽$/, "",);
};

export function QrPaymentModal({ onClose, block, }: IQrPaymentModalProps,) {
  const { paymentInfo, } = useQrPaymentMutation();
  const paymentMutation = useMutation({
    mutationFn: qrPaymentMutationFn,
  },);

  const [customAmountRub, setCustomAmountRub,] = useState(0,);
  const customAmountValue = formatAmountWithoutCurrency(customAmountRub,);
  const customAmountDigitsCount = customAmountValue.replace(/\s/ig, "",).length;
  const customAmountGroupsCount = (customAmountValue.match(/\s/ig,) ?? []).length;
  const customAmountWidthCh = Math.max(1, customAmountDigitsCount + (customAmountGroupsCount * 0.4),);

  const hasPaymentResult = paymentMutation.isSuccess;
  const isSuccessPayment = hasPaymentResult && (paymentMutation.data.status === "SUCCESS");
  const isRejectedPayment = hasPaymentResult && (paymentMutation.data.status === "REJECTED");
  const isAmountInputLocked = paymentMutation.isPending || isRejectedPayment;
  const amountTextColor = isAmountInputLocked ? "gray.500" : "black";

  useEffect(() => {
    block?.(paymentMutation.isPending,);
  }, [paymentMutation.isPending,],);

  useEffect(() => {
    if (paymentInfo.isError) {
      onClose?.();
    }
  }, [paymentInfo.isError,],);

  useEffect(() => {
    if (paymentInfo.data?.amountCustom) {
      setCustomAmountRub(paymentInfo.data.amount > 0 ? Math.floor(paymentInfo.data.amount / 100,) : 0,);
    }
  }, [paymentInfo.data?.amount, paymentInfo.data?.amountCustom,],);

  const handleCustomAmountChange = (value: string,): void => {
    const onlyDigits = value.replace(/\D/ig, "",);
    setCustomAmountRub(onlyDigits ? parseInt(onlyDigits,) : 0,);
  };

  const handlePaymentClick = (): void => {
    if (paymentInfo.data) {
      paymentMutation.mutate({
        paymentId: paymentInfo.data.id,
        precheckId: paymentInfo.data.precheckId,
        ...(paymentInfo.data.amountCustom ? { amount: customAmountRub * 100, } : {}),
      },);
    }
  };

  if (paymentInfo.isLoading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Box pt={8} pb={17}>
        <Center>
          <Box>
            {paymentInfo.data?.amountCustom
              ? (
                <Center>
                  <Box borderBottom="1px solid" borderColor="blackAlpha.700" pb="2px" mb="8px">
                    <HStack spacing={1} alignItems="baseline">
                      <Input
                        autoFocus
                        variant="unstyled"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        textAlign="center"
                        fontSize="8xl"
                        fontWeight="bold"
                        color={amountTextColor}
                        value={customAmountValue}
                        onChange={(e,) => {
                          handleCustomAmountChange(e.currentTarget.value,);
                        }}
                        isDisabled={isAmountInputLocked}
                        w={`${String(customAmountWidthCh,)}ch`}
                        minW="1ch"
                        flex="0 0 auto"
                        p={0}
                        _disabled={{
                          color: amountTextColor,
                          opacity: 1,
                        }}
                      />
                      <Text fontSize="8xl" fontWeight="bold" color={amountTextColor} flexShrink={0}>₽</Text>
                    </HStack>
                  </Box>
                </Center>
                )
              : (
                <Heading textAlign="center" fontSize="8xl">
                  {formatPrice((paymentInfo.data?.amount ?? 0) / 100, 0,)}
                </Heading>
                )}
            <Text textAlign="center">
              Платёж за покупку
              <br />
              в
              {" "}
              {paymentInfo.data?.description}
            </Text>
            {isSuccessPayment && (
              <HStack spacing={2} py={6} display="flex" justifyContent="center">
                <Icon as={CheckCircleIcon} boxSize={6} color="success" />
                <Text color="success">Оплачено успешно</Text>
              </HStack>
            )}
            {isRejectedPayment && (
              <HStack spacing={2} py={6} display="flex" justifyContent="center">
                <Icon as={ExclamationCircleIcon} boxSize={6} color="error" />
                <Text color="error">Оплата не прошла, попробуйте ещё раз</Text>
              </HStack>
            )}
          </Box>
        </Center>
      </Box>
      <Box pt={4}>
        {!hasPaymentResult && (
          <Button
            w="100%"
            colorScheme="brand"
            isLoading={paymentMutation.isPending}
            isDisabled={paymentInfo.isError}
            onClick={handlePaymentClick}
          >
            Оплатить
          </Button>
        )}
        {hasPaymentResult && (
          <Button w="100%" colorScheme="gray" onClick={onClose}>Закрыть</Button>
        )}
      </Box>
    </div>
  );
}
